import { Autocomplete, Box, GlobalStyles, TextField, createFilterOptions } from '@mui/material';
import { AppRoutes } from '@rekindle/shared/constants/appRoutes';
import glyphMap from '../assets/notification-icons/glyphMap';
import iconFontUrl from '../assets/notification-icons/MaterialIcons.ttf';
import { useAdminText } from './shared/useAdminText';

type IconName = keyof typeof glyphMap;
const icons = (Object.keys(glyphMap) as IconName[]).sort();
const routes = [...new Set(Object.values(AppRoutes))].sort();
// Keep opening the menu responsive; every icon remains available through search.
const filterIcons = createFilterOptions<IconName>({ limit: 100 });

function IconPreview({ name }: { name: IconName }) {
  return (
    <Box component="span" aria-hidden="true" data-notification-icon={name}
      sx={{ fontFamily: 'RekindleNotificationIcons', fontSize: 24, lineHeight: 1, fontWeight: 'normal', fontStyle: 'normal', flexShrink: 0, mr: 1 }}>
      {String.fromCodePoint(glyphMap[name])}
    </Box>
  );
}

interface NotificationOptionsProps {
  icon: string;
  route: string;
  onIconChange: (value: string) => void;
  onRouteChange: (value: string) => void;
  disabled?: boolean;
}

export function NotificationOptions({ icon, route, onIconChange, onRouteChange, disabled }: NotificationOptionsProps) {
  const text = useAdminText();
  const selectedIcon = Object.prototype.hasOwnProperty.call(glyphMap, icon) ? icon as IconName : null;
  const selectedRoute = routes.find(value => value === route) ?? null;
  return (
    <>
      <GlobalStyles styles={{ '@font-face': {
        fontFamily: 'RekindleNotificationIcons', src: `url("${iconFontUrl}") format("truetype")`, fontDisplay: 'block',
      } }} />
      <Autocomplete
        fullWidth disabled={disabled} options={icons} value={selectedIcon} filterOptions={filterIcons}
        onChange={(_, value) => onIconChange(value ?? '')}
        clearText={text('notifications.clearIcon', 'Use default icon')}
        openText={text('notifications.openIcons', 'Choose icon')}
        closeText={text('notifications.closeOptions', 'Close options')}
        noOptionsText={text('notifications.noIcons', 'No matching icons')}
        renderOption={({ key, ...props }, option) => (
          <Box component="li" key={key} {...props}>
            <IconPreview name={option} />{option}
          </Box>
        )}
        renderInput={params => (
          <TextField {...params} label={text('notifications.icon', 'Icon')}
            placeholder={text('notifications.defaultIcon', 'Default (campaign)')}
            helperText={text('notifications.iconHelp', 'Search all icons by name (up to 100 matches). Shown in the in-app notification list.')}
            slotProps={{ input: { ...params.InputProps, startAdornment: <>
              <IconPreview name={selectedIcon ?? 'campaign'} />{params.InputProps.startAdornment}
            </> } }} />
        )}
        sx={{ mb: 2 }}
      />
      <Autocomplete
        fullWidth disabled={disabled} options={routes} value={selectedRoute}
        onChange={(_, value) => onRouteChange(value ?? '')}
        clearText={text('notifications.clearRoute', 'Remove destination')}
        openText={text('notifications.openRoutes', 'Choose destination')}
        closeText={text('notifications.closeOptions', 'Close options')}
        noOptionsText={text('notifications.noRoutes', 'No matching routes')}
        renderOption={({ key, ...props }, option) => (
          <Box component="li" key={key} {...props} sx={{ overflowWrap: 'anywhere' }}>{option}</Box>
        )}
        renderInput={params => (
          <TextField {...params} label={text('notifications.route', 'Deep Link Route')}
            placeholder={text('notifications.noDestination', 'No destination')}
            helperText={text('notifications.routeHelp', 'Screen to open when the notification is tapped. / is Home.')} />
        )}
        sx={{ mb: 3 }}
      />
    </>
  );
}
